import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { AuthContext } from '../AuthContext';
import './AgeVerification.css';

const AgeVerification = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [month, setMonth] = useState('');
    const [day, setDay] = useState('');
    const [year, setYear] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const currentYear = new Date().getFullYear();
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    // Days 1-31 (we validate the actual date on submit)
    const days = Array.from({ length: 31 }, (_, i) => i + 1);
    // Years from 100 years ago up to the max allowed (18 years ago)
    const years = Array.from(
        { length: currentYear - 18 - (currentYear - 100) + 1 },
        (_, i) => currentYear - 18 - i
    );

    const handleConfirm = async (e) => {
        e.preventDefault();
        setError('');

        if (!month || !day || !year) {
            setError('Please select your full date of birth.');
            return;
        }

        const birth = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

        // Catch invalid dates like Feb 31
        if (
            birth.getFullYear() !== parseInt(year) ||
            birth.getMonth() !== parseInt(month) - 1 ||
            birth.getDate() !== parseInt(day)
        ) {
            setError('Please enter a valid date.');
            return;
        }

        const today = new Date();
        const ageDiff = today - birth;
        const ageYears = ageDiff / (1000 * 60 * 60 * 24 * 365.25);

        if (ageYears < 18) {
            await signOut(auth);
            navigate('/', { state: { ageBlocked: true } });
            return;
        }

        // 18+ — write verification record, discard the birthdate
        try {
            setLoading(true);
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                isAgeVerified: true,
                ageVerifiedAt: serverTimestamp(),
                // birthdate is intentionally NOT stored
            });
            window.location.replace('/auth-home');
        } catch (err) {
            console.error('Error saving age verification:', err);
            setError('Something went wrong. Please try again.');
            setLoading(false);
        }
    };

    return (
        <div className="age-verification-page">
            <div className="age-verification-card">
                <div className="age-verification-logo">DANNYTOPIA</div>

                <h1>Age Verification Required</h1>
                <p className="age-verification-subtext">
                    This website contains adult content intended for individuals 18 years of age or older.
                    Please confirm your date of birth to continue.
                </p>

                <form onSubmit={handleConfirm} className="age-verification-form">
                    <label>Date of Birth</label>
                    <div className="dob-selects">
                        <select
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            required
                        >
                            <option value="">Month</option>
                            {months.map((m, i) => (
                                <option key={m} value={i + 1}>{m}</option>
                            ))}
                        </select>

                        <select
                            value={day}
                            onChange={(e) => setDay(e.target.value)}
                            required
                        >
                            <option value="">Day</option>
                            {days.map(d => (
                                <option key={d} value={d}>{d}</option>
                            ))}
                        </select>

                        <select
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            required
                        >
                            <option value="">Year</option>
                            {years.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {error && <p className="age-verification-error">{error}</p>}

                    <button
                        type="submit"
                        className="age-verify-btn"
                        disabled={loading || !month || !day || !year}
                    >
                        {loading ? 'Verifying...' : 'I am 18 or older — Enter'}
                    </button>
                </form>

                <p className="age-verification-disclaimer">
                    By entering, you confirm that you are at least 18 years of age and consent
                    to viewing adult content. Your date of birth is used only to verify your age
                    and is not stored. A verification record is kept in accordance with our{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                    </a>.
                </p>

                <div className="age-verification-exit">
                    <button
                        type="button"
                        className="age-exit-btn"
                        onClick={async () => {
                            await signOut(auth);
                            navigate('/');
                        }}
                    >
                        I cannot verify my age — Return to Sign In
                    </button>
                    <p className="age-exit-contact">
                        Questions or issues accessing your account?{' '}
                        <a href="mailto:dannytopia.llc@gmail.com">dannytopia.llc@gmail.com</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AgeVerification;
